import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { VisionManagement } from '@/components/alignment/VisionManagement';
import { ObjectivesManagement } from '@/components/alignment/ObjectivesManagement';
import { StrategyManagement } from '@/components/alignment/StrategyManagement';
import { ProjectsManagement } from '@/components/alignment/ProjectsManagement';
import { AlignmentReports } from '@/components/alignment/AlignmentReports';
import { AlignmentWidget } from '@/components/dashboard/AlignmentWidget';
import { AlignmentRoadmapSelect } from '@/components/alignment/AlignmentRoadmapPicker';
import { AlignmentRoadmapScopeProvider } from '@/components/alignment/AlignmentRoadmapScopeContext';
import { useAuth } from '@/state/auth/AuthProvider';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  TagIcon, 
  EyeIcon, 
  ChartBarIcon,
  ChartPieIcon,
  FolderIcon,
  FlagIcon
} from '@heroicons/react/24/outline';

type AlignmentSection = 'overview' | 'visions' | 'objectives' | 'strategies' | 'projects' | 'reports';

function AlignmentPageContent() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState<AlignmentSection>('overview');

  // Set active section based on URL path
  useEffect(() => {
    const path = location.pathname;
    if (path === '/alignment' || path === '/alignment/') {
      setActiveSection('overview');
    } else if (path === '/alignment/visions') {
      setActiveSection('visions');
    } else if (path === '/alignment/objectives') {
      setActiveSection('objectives');
    } else if (path === '/alignment/strategies') {
      setActiveSection('strategies');
    } else if (path === '/alignment/projects') {
      setActiveSection('projects');
    } else if (path === '/alignment/reports') {
      setActiveSection('reports');
    } else {
      setActiveSection('overview');
    }
  }, [location.pathname]);





  const sections = [
    { id: 'overview', name: t('alignment.overview'), icon: TagIcon, path: '/alignment' },
    { id: 'visions', name: t('alignment.visionManagement'), icon: EyeIcon, path: '/alignment/visions' },
    { id: 'objectives', name: t('alignment.objectivesManagement'), icon: ChartBarIcon, path: '/alignment/objectives' },
    { id: 'strategies', name: t('alignment.strategyManagement'), icon: ChartBarIcon, path: '/alignment/strategies' },
    { id: 'projects', name: t('alignment.projectsManagement'), icon: FolderIcon, path: '/alignment/projects' },
    { id: 'reports', name: t('alignment.alignmentReports'), icon: ChartPieIcon, path: '/alignment/reports' }
  ];

  const handleSectionChange = (sectionId: AlignmentSection) => {
    setActiveSection(sectionId);
    // Navigate to the corresponding route
    const section = sections.find(s => s.id === sectionId);
    if (section && section.path !== location.pathname) {
      window.history.pushState({}, '', section.path);
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-8">
            <AlignmentWidget tenantId={user?.tenantId || ''} />
            
            {/* Quick Actions */}
            <Card variant="outline">
              <CardHeader>
                <CardTitle className="text-[var(--color-surface-text)]">{t('alignment.quickActions')}</CardTitle>
                <CardDescription className="text-[var(--color-surface-text)] opacity-75">
                  {t('alignment.quickActionsDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button
                    onClick={() => handleSectionChange('visions')}
                    className="p-4 border border-[var(--color-border)] rounded-lg hover:border-[var(--color-accent-bg)] hover:bg-[var(--color-accent-bg)]/20 transition-colors text-left group"
                  >
                    <EyeIcon className="w-8 h-8 mb-2 text-[var(--color-surface-text)] group-hover:text-[var(--color-accent-bg)] transition-colors" />
                    <h3 className="font-semibold text-[var(--color-surface-text)] group-hover:text-[var(--color-accent-bg)] transition-colors">{t('alignment.createVision')}</h3>
                    <p className="text-sm opacity-75 text-[var(--color-secondary-text)] group-hover:text-[var(--color-accent-bg)] group-hover:opacity-90 transition-colors">{t('alignment.createVisionDescription')}</p>
                  </button>

                  <button
                    onClick={() => handleSectionChange('objectives')}
                    className="p-4 border border-[var(--color-border)] rounded-lg hover:border-[var(--color-accent-bg)] hover:bg-[var(--color-accent-bg)]/20 transition-colors text-left group"
                  >
                    <FlagIcon className="w-8 h-8 mb-2 text-[var(--color-surface-text)] group-hover:text-[var(--color-accent-bg)] transition-colors" />
                    <h3 className="font-semibold text-[var(--color-surface-text)] group-hover:text-[var(--color-accent-bg)] transition-colors">{t('alignment.createObjective')}</h3>
                    <p className="text-sm opacity-75 text-[var(--color-secondary-text)] group-hover:text-[var(--color-accent-bg)] group-hover:opacity-90 transition-colors">{t('alignment.createObjectiveDescription')}</p>
                  </button>
                  
                  <button
                    onClick={() => handleSectionChange('strategies')}
                    className="p-4 border border-[var(--color-border)] rounded-lg hover:border-[var(--color-accent-bg)] hover:bg-[var(--color-accent-bg)]/20 transition-colors text-left group"
                  >
                    <ChartBarIcon className="w-8 h-8 mb-2 text-[var(--color-surface-text)] group-hover:text-[var(--color-accent-bg)] transition-colors" />
                    <h3 className="font-semibold text-[var(--color-surface-text)] group-hover:text-[var(--color-accent-bg)] transition-colors">{t('alignment.createStrategy')}</h3>
                    <p className="text-sm opacity-75 text-[var(--color-secondary-text)] group-hover:text-[var(--color-accent-bg)] group-hover:opacity-90 transition-colors">{t('alignment.createStrategyDescription')}</p>
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        );
        
      case 'visions':
        return <VisionManagement />;
        
      case 'objectives':
        return <ObjectivesManagement />;
        
      case 'strategies':
        return <StrategyManagement />;
        
      case 'projects':
        return <ProjectsManagement />;
        
      case 'reports':
        return <AlignmentReports />;
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="p-4">
          {/* Header without dark background */}
          <div className="mb-2">
            <div className="mb-2">
              <div className="flex items-center gap-2">
                <div className="p-1 rounded-lg">
                  <TagIcon className="w-5 h-5 text-[var(--color-info)]" />
                </div>
                <h1 className="text-xl font-bold text-[var(--color-surface-text)]">
                  {t('alignment.title')}
                </h1>
              </div>
              <div className="mt-1.5 pl-9">
                <AlignmentRoadmapSelect />
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="border-b border-[var(--color-border)]">
              <nav className="-mb-px flex space-x-8 overflow-x-auto">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  
                  
                  return (
                    <button
                      key={section.id}
                      onClick={() => handleSectionChange(section.id as AlignmentSection)}
                      className={`group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                        isActive
                          ? 'border-[var(--color-accent-bg)] text-[var(--color-accent-bg)]'
                          : 'border-transparent text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] hover:border-[var(--color-border)]'
                      }`}
                    >
                      <Icon className={`-ml-0.5 mr-2 h-5 w-5 ${
                        isActive ? 'text-[var(--color-accent-bg)]' : 'text-[var(--color-secondary-text)] group-hover:text-[var(--color-surface-text)]'
                      }`} />
                      {section.name}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          {renderSection()}
        </div>
      </div>
    </div>
  );
}

export default function AlignmentPage() {
  const { user } = useAuth();
  if (!user?.tenantId) {
    return <AlignmentPageContent />;
  }
  return (
    <AlignmentRoadmapScopeProvider tenantId={user.tenantId}>
      <AlignmentPageContent />
    </AlignmentRoadmapScopeProvider>
  );
}
